import { useState, type CSSProperties, type ReactNode } from "react";
import {
  ArrowRight,
  Check,
  Copy,
} from "lucide-react";
import SlideGuide, {
  type GuideSlide,
} from "@/components/training/SlideGuide";
import {
  FUTURE_OF_WORK_VIDEO_URL,
  S2P_COUNCIL_SLIDES,
  S2P_COUNCIL_STEPS,
} from "@/data/s2pAIBusinessCouncilAugust";

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const ADAPTIVE_REASONING_PROTOCOL = `Use the Adaptive Reasoning Protocol:

Assess the request type, then apply the appropriate method:

If solving, analyzing, or debugging → Atom of Thought: Decompose into atomic reasoning units. For each atom:

State the logical component
Validate independence
Verify correctness
Then synthesize atoms into final answer.

If explaining, learning, or teaching → Feynman Loop: Explain as if teaching a curious beginner. For each cycle:

Use a concrete analogy
Flag confusion points
Ask questions that reveal gaps
Then compress into a teachable snapshot.

If both are needed → Chain them: First solve via Atom of Thought, then explain the solution via Feynman Loop.`;

const GEAR_VISUAL_ITEMS = [
  { number: 0, title: "Human judgement drives every gear", body: "Sets direction · reviews results", color: "#7A869E", accent: "#3E4A63", transform: "translate(95 330) scale(1.45)", rotor: "human", numberSize: 22 },
  { number: 1, title: "Chat", body: "One question or idea · quick burst", color: "#F0A46A", accent: "#B45309", transform: "translate(240 274) scale(1.28)", rotor: "chat", numberSize: 24 },
  { number: 2, title: "Assistant", body: "One finished deliverable · sustained help", color: "#EBCB55", accent: "#A16207", transform: "translate(350 202) scale(1.05)", rotor: "assistant", numberSize: 25 },
  { number: 3, title: "Agents", body: "Multi-step objective · delegated run", color: "#5FC6D8", accent: "#0E7490", transform: "translate(442 141) scale(.86)", rotor: "agents", numberSize: 27 },
  { number: 4, title: "Loops", body: "Recurring outcome · continuous system", color: "#77C58A", accent: "#047857", transform: "translate(515 89) scale(.68)", rotor: "loops", numberSize: 29 },
] as const;

const GEAR_CONNECTORS = [
  { number: 4, path: "M552 89 L603 72 L630 72", color: "#77C58A" },
  { number: 3, path: "M489 141 L603 145 L630 145", color: "#5FC6D8" },
  { number: 2, path: "M409 202 L603 218 L630 218", color: "#EBCB55" },
  { number: 1, path: "M311 274 L603 291 L630 291", color: "#F0A46A" },
  { number: 0, path: "M177 330 L603 378 L630 378", color: "#A8B2C4" },
] as const;

const GEAR_LABELS = [
  { number: 4, titleY: 68, bodyY: 94 },
  { number: 3, titleY: 141, bodyY: 167 },
  { number: 2, titleY: 214, bodyY: 240 },
  { number: 1, titleY: 287, bodyY: 313 },
  { number: 0, titleY: 374, bodyY: 400 },
] as const;

const AGENT_SKILL_PLAYBOOK = [
  ["Outcome", "What result should the agent produce?"],
  ["Sources", "What approved context should it use?"],
  ["Method", "How does your team do the work?"],
  ["Quality", "What does good look like?"],
  ["Stop rule", "When should it pause and ask?"],
] as const;

const AGENT_SKILL_EXAMPLE = [
  ["Trigger", "Before every leadership meeting"],
  ["Sources", "Calendar, prior notes, priorities, and project updates"],
  ["Instructions", "Surface decisions, risks, blockers, and unresolved commitments"],
  ["Output", "A one-page meeting brief"],
  ["Guardrail", "Read-only—never message people or change records"],
] as const;

const AgentSkillsExplainerSlide = () => (
  <div
    data-layout="agent-skills-explainer"
    className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        backgroundImage:
          "radial-gradient(circle at 88% 12%, rgba(0,71,255,.10), transparent 28%), radial-gradient(circle at 8% 90%, rgba(95,198,216,.14), transparent 30%), linear-gradient(rgba(19,33,58,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(19,33,58,.03) 1px, transparent 1px)",
        backgroundSize: "auto, auto, 64px 64px, 64px 64px",
      }}
    />

    <div className="relative mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 lg:grid-cols-[.94fr_1.06fr] lg:items-center lg:gap-12 lg:py-6">
      <div>
        <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#0047FF] sm:text-sm lg:text-base">
          <span className="h-0.5 w-12 bg-[#0047FF]" aria-hidden="true" />
          What AI Agent Skills Are
        </p>
        <h1 className="s2p-editorial mt-3 max-w-[13ch] text-[clamp(2.25rem,4vw,4.75rem)] font-semibold leading-[.96] tracking-[-0.05em]">
          Skills turn team expertise into repeatable AI behavior.
        </h1>
        <p className="mt-3 text-base font-semibold leading-relaxed text-[#344258] sm:text-lg lg:text-xl">
          An agent is the teammate. A Skill is the playbook.
        </p>

        <div className="mt-4 flex items-stretch gap-2" aria-label="Agent to Skill to reliable result">
          {[
            ["Agent", "Capable teammate"],
            ["Skill", "Reusable playbook"],
            ["Result", "Reliable output"],
          ].map(([title, body], index) => (
            <div key={title} className="contents">
              <div className={`min-w-0 flex-1 rounded-xl border p-2.5 ${index === 1 ? "border-[#0047FF]/30 bg-[#EAF0FF]" : "border-[#D8E0EC] bg-white"}`}>
                <p className={`text-xs font-extrabold uppercase tracking-[0.13em] ${index === 1 ? "text-[#0047FF]" : "text-[#68758A]"}`}>{title}</p>
                <p className="mt-1 text-sm font-bold leading-snug text-[#13213A] sm:text-base">{body}</p>
              </div>
              {index < 2 && <ArrowRight className="mt-5 h-5 w-5 flex-shrink-0 text-[#E4691E]" aria-hidden="true" />}
            </div>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-1 border-t border-[#D8E0EC] pt-3">
          {AGENT_SKILL_PLAYBOOK.map(([title, body]) => (
            <div key={title} className="leading-snug last:col-span-2">
              <strong className="text-[13px] text-[#0047FF]">{title}:</strong>{" "}
              <span className="text-[13px] text-[#536078]">{body}</span>
            </div>
          ))}
        </div>
      </div>

      <article className="overflow-hidden rounded-2xl border border-[#D8E0EC] bg-white shadow-xl shadow-[#13213A]/10">
        <div className="border-b border-[#D8E0EC] bg-[#EAF0FF] px-5 py-4 sm:px-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#0047FF]">Example Skill</p>
          <h2 className="s2p-editorial mt-2 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">Leadership Meeting Brief</h2>
        </div>
        <dl className="px-5 sm:px-6">
          {AGENT_SKILL_EXAMPLE.map(([term, description]) => (
            <div key={term} className="grid grid-cols-[112px_1fr] gap-4 border-b border-[#E2E7EF] py-3 last:border-b-0 sm:grid-cols-[130px_1fr]">
              <dt className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#0E7490] sm:text-sm">{term}</dt>
              <dd className="text-sm font-medium leading-relaxed text-[#344258] sm:text-base">{description}</dd>
            </div>
          ))}
        </dl>
        <p className="border-t border-[#D8E0EC] bg-[#FFF8DB] px-5 py-4 text-base font-semibold text-[#4D4632] sm:px-6 sm:text-lg">
          Teach it once. Reuse it. Improve it after every run.
        </p>
      </article>
    </div>
  </div>
);

const ToolToTeamThinkingSlide = () => (
  <div
    data-layout="ten-x-tool-to-team"
    className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        backgroundImage: "radial-gradient(circle at 82% 20%, rgba(0,71,255,.10), transparent 28%), radial-gradient(circle at 8% 92%, rgba(95,198,216,.14), transparent 28%), linear-gradient(rgba(19,33,58,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(19,33,58,.035) 1px, transparent 1px)",
        backgroundSize: "auto, auto, 4vw 4vw, 4vw 4vw",
      }}
    />
    <span className="pointer-events-none absolute -bottom-[24%] right-[2%] font-sans text-[clamp(18rem,38vw,44rem)] font-extrabold leading-[.78] tracking-[-.12em] text-[#0047FF]/[0.055]" aria-hidden="true">1</span>

    <div className="relative mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-5 sm:py-7">
      <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#0047FF] sm:text-sm lg:text-base">
        <span className="h-0.5 w-12 bg-[#0047FF]" />The Biggest Mistake
      </p>
      <h1 className="s2p-editorial mt-5 max-w-[15ch] text-[clamp(3rem,5.4vw,6.35rem)] font-semibold leading-[.98] tracking-[-0.05em] text-[#13213A]">
        Treating AI like a tool instead of a team.
      </h1>

      <div className="mt-7 grid items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr] lg:gap-7">
        <article className="border border-[#D8E0EC] bg-white p-5 shadow-sm sm:p-7 lg:p-8">
          <h2 className="s2p-editorial text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl lg:text-5xl">Tool thinking</h2>
          <p className="mt-4 text-lg leading-relaxed text-[#536078] sm:text-xl lg:text-2xl">“I need to make this thing produce an answer.”</p>
        </article>

        <div className="grid place-items-center text-[#FF7420]" aria-hidden="true">
          <ArrowRight className="h-12 w-12 rotate-90 stroke-[1.5] lg:h-16 lg:w-16 lg:rotate-0" />
        </div>

        <article className="border border-[#0047FF]/30 bg-[#EAF0FF] p-5 shadow-sm sm:p-7 lg:p-8">
          <h2 className="s2p-editorial text-3xl font-semibold leading-tight tracking-[-0.035em] sm:text-4xl lg:text-5xl">Team thinking</h2>
          <p className="mt-4 text-lg leading-relaxed text-[#3E4D66] sm:text-xl lg:text-2xl">“I need to define the outcome, context, boundaries, and review.”</p>
        </article>
      </div>

      <div className="mt-4 grid gap-1 border-t border-[#D8E0EC] pt-3 sm:grid-cols-[auto_1fr] sm:items-baseline sm:gap-5">
        <p className="s2p-editorial text-xl font-semibold text-[#C8550A] sm:text-2xl lg:text-3xl">Think Roomba, not remote control.</p>
        <p className="text-base leading-relaxed text-[#536078] sm:text-lg lg:text-xl">A Roomba owns the objective: clean the floor. You set the boundaries and review the result instead of deciding which room comes next and which vacuuming pattern to use.</p>
      </div>
    </div>
  </div>
);

const AtomicReasoningSlide = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyText(ADAPTIVE_REASONING_PROTOCOL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      data-layout="editorial-prompt"
      className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(circle at 82% 18%, rgba(0,71,255,.10), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(19,33,58,.025) 60% 62%, transparent 62% 68%, rgba(19,33,58,.025) 68% 70%, transparent 70%)",
        }}
      />

      <div className="relative mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 lg:grid-cols-[minmax(420px,.72fr)_minmax(680px,1.28fr)] lg:items-center lg:gap-12 lg:py-7">
        <div>
          <header className="max-w-[1500px]">
            <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#0047FF] sm:text-sm lg:text-base">
              <span className="h-0.5 w-12 bg-[#0047FF]" aria-hidden="true" />
              10× your AI answers
            </p>
            <h1 className="s2p-editorial mt-3 text-[clamp(2.5rem,4.35vw,5.15rem)] font-bold leading-[0.96] tracking-[-0.045em] text-[#13213A]">
              Atomic Reasoning
            </h1>
            <p className="mt-4 max-w-6xl text-base font-medium leading-relaxed text-[#536078] sm:text-lg lg:text-xl xl:text-2xl">
              Give the agent a repeatable way to reason and explain. Place this in the primary surface’s system-prompt or custom-instructions field, then ask what instructions it is operating under.
            </p>
          </header>

          <p className="mt-5 border-l-4 border-[#0047FF] bg-[#EAF0FF] p-4 text-sm font-medium leading-relaxed text-[#344258] sm:text-base lg:text-lg">
            <strong>Remember:</strong> The agent reports the protocol and returns substantively different approaches instead of one linear answer.
          </p>
        </div>

        <div className="min-w-0">
          <div className="overflow-hidden rounded-2xl border border-[#D8E0EC] bg-white text-[#13213A] shadow-xl shadow-[#13213A]/10">
            <div className="flex items-center justify-between gap-4 border-b border-[#D8E0EC] px-4 py-3 sm:px-5">
              <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#0047FF] sm:text-sm lg:text-base">
                Adaptive Reasoning Protocol
              </p>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex min-h-10 flex-shrink-0 items-center gap-2 rounded-full bg-[#0047FF] px-4 py-2 text-sm font-bold text-white hover:bg-[#0039CC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5FC6D8]"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="max-h-[46svh] overflow-auto whitespace-pre-wrap bg-[#F3F6FC] px-4 py-5 font-mono text-sm leading-7 text-[#25344D] sm:px-5 sm:text-base lg:text-lg">
              {ADAPTIVE_REASONING_PROTOCOL}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

const CONNECTIONS_AND_TOOLS_SECTIONS = [
  { label: "01", title: "Connect sources", body: "Calendar, email, docs, analytics, code.", color: "#C8550A" },
  { label: "02", title: "Connect actions", body: "Search, draft, edit, run, verify.", color: "#0E7490" },
  { label: "03", title: "Set boundaries", body: "Read, draft, change, publish.", color: "#0047FF" },
] as const;

const ConnectionsAndToolsSlide = () => (
  <div
    data-layout="editorial-concept"
    className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        backgroundImage:
          "radial-gradient(circle at 82% 18%, rgba(0,71,255,.10), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(19,33,58,.025) 60% 62%, transparent 62% 68%, rgba(19,33,58,.025) 68% 70%, transparent 70%)",
      }}
    />

    <div className="relative mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 lg:grid-cols-[minmax(420px,.78fr)_minmax(640px,1.22fr)] lg:items-center lg:gap-12 lg:py-7">
      <div>
        <header className="max-w-[1500px]">
          <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.22em] text-[#0047FF] sm:text-sm lg:text-base">
            <span className="h-0.5 w-9 bg-[#0047FF] sm:w-12" aria-hidden="true" />
            Give the agent only what the work needs
          </p>
          <h1 className="s2p-editorial mt-3 text-[clamp(3rem,5.35vw,6.25rem)] font-bold leading-[0.96] tracking-[-0.045em] text-[#13213A]">
            Set up Connections &amp; Tools
          </h1>
          <p className="mt-4 max-w-6xl text-base font-medium leading-relaxed text-[#536078] sm:text-lg lg:text-xl xl:text-2xl">
            Give the team eyes, hands, and a clearly bounded workspace.
          </p>
        </header>

        <p className="mt-5 border-l-4 border-[#0047FF] bg-[#EAF0FF] p-4 text-sm font-medium leading-relaxed text-[#344258] sm:text-base lg:text-lg">
          <strong>Remember:</strong> Start with the sources the workflow already relies on. Add only the actions needed to complete the work. Be explicit about which actions are read-only, reversible, approval-gated, or external.
        </p>
      </div>

      <div className="min-w-0 border-y border-[#D8E0EC]">
        {CONNECTIONS_AND_TOOLS_SECTIONS.map((section) => (
          <article
            key={section.title}
            className="grid grid-cols-[minmax(112px,.38fr)_1fr] gap-3 border-b border-[#D8E0EC] py-3 last:border-b-0 sm:grid-cols-[minmax(140px,.38fr)_minmax(190px,.62fr)_1.1fr] sm:items-baseline sm:gap-6 sm:py-5"
          >
            <span
              className="s2p-display break-words text-lg font-bold uppercase leading-[.95] tracking-[0.02em] sm:text-xl lg:text-2xl"
              style={{ color: section.color }}
            >
              {section.label}
            </span>
            <h2 className="s2p-display text-xl font-semibold uppercase leading-tight text-[#13213A] sm:text-2xl lg:text-3xl">
              {section.title}
            </h2>
            <div className="col-start-2 sm:col-start-3">
              <p className="text-base leading-relaxed text-[#536078] sm:text-lg lg:text-xl">{section.body}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </div>
);

const AI_TEAM_MANAGEMENT_PRINCIPLES = [
  { title: "Context", body: "What matters here?" },
  { title: "Direction", body: "What outcome do we own?" },
  { title: "Feedback", body: "What should repeat next time?" },
] as const;

const AITeamManagementModelSlide = () => (
  <div
    data-layout="ten-x-management-model"
    className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#171F2F] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        backgroundImage: "radial-gradient(circle at 8% 16%, rgba(255,116,32,.09), transparent 22%), linear-gradient(90deg, transparent 49.8%, rgba(10,31,68,.045) 50%, transparent 50.2%)",
      }}
    />
    <span className="pointer-events-none absolute bottom-0 left-0 h-1 w-[24%] bg-[#FF7420]" aria-hidden="true" />

    <div className="relative mx-auto grid min-h-full w-full max-w-[1760px] items-center gap-8 py-6 lg:grid-cols-[1.25fr_.75fr] lg:gap-[6.5%] lg:py-8">
      <div>
        <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#C8550A] sm:text-sm lg:text-base">
          <span className="h-0.5 w-12 bg-[#C8550A]" aria-hidden="true" />The management model
        </p>
        <h1 className="s2p-editorial mt-5 max-w-[13ch] text-[clamp(3rem,5.5vw,6.5rem)] font-semibold leading-[.98] tracking-[-0.05em] text-[#171F2F]">
          AI teams improve the same way people do.
        </h1>
      </div>

      <ol className="grid gap-4 sm:gap-5 lg:gap-6">
        {AI_TEAM_MANAGEMENT_PRINCIPLES.map((principle, index) => (
          <li key={principle.title} className="grid grid-cols-[2.25rem_1fr] items-start gap-3 sm:grid-cols-[2.75rem_1fr] sm:gap-4">
            <span className="pt-0.5 text-lg font-extrabold tabular-nums text-[#FF7420] sm:text-xl lg:text-2xl">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span>
              <strong className="block text-2xl font-extrabold leading-tight text-[#171F2F] sm:text-3xl lg:text-4xl">{principle.title}</strong>
              <span className="mt-1 block text-lg leading-snug text-[#344258] sm:text-xl lg:text-2xl">{principle.body}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

const THREE_LEADERSHIP_LAYERS = [
  { label: "S", title: "Strategy", body: "Know what AI can do, where it breaks, and what is arriving next.", color: "#C8550A" },
  { label: "D", title: "Decisions", body: "Supply role, goal, constraints, context, taste, and prior attempts.", color: "#0E7490" },
  { label: "J", title: "Judgement", body: "Choose which plausible answer fits your customer, culture, and moment.", color: "#0047FF" },
] as const;

const ThreeLeadershipLayersSlide = () => (
  <div className="relative -mx-5 flex min-h-full w-[calc(100%+2.5rem)] overflow-hidden bg-[#F7F8FB] px-5 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12">
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        backgroundImage: "radial-gradient(circle at 82% 18%, rgba(0,71,255,.10), transparent 28%), linear-gradient(115deg, transparent 60%, rgba(19,33,58,.025) 60% 62%, transparent 62% 68%, rgba(19,33,58,.025) 68% 70%, transparent 70%)",
      }}
    />
    <div data-layout="editorial-concept" className="relative mx-auto grid min-h-full w-full max-w-[1760px] gap-7 py-5 lg:grid-cols-[minmax(420px,.78fr)_minmax(640px,1.22fr)] lg:items-center lg:gap-12 lg:py-7">
      <div>
        <header className="max-w-[1500px]">
          <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.22em] text-[#0047FF] sm:text-sm lg:text-base">
            <span className="h-0.5 w-9 bg-[#0047FF] sm:w-12" aria-hidden="true" />
            The leadership model
          </p>
          <h1 className="s2p-editorial mt-3 text-[clamp(3rem,5.35vw,6.25rem)] font-bold leading-[0.96] tracking-[-0.045em] text-[#13213A]">
            Understanding has three layers
          </h1>
        </header>
      </div>

      <div className="min-w-0">
        <div className="border-y border-[#D8E0EC]">
          {THREE_LEADERSHIP_LAYERS.map((layer) => (
            <article key={layer.title} className="grid grid-cols-[minmax(112px,.38fr)_1fr] gap-3 border-b border-[#D8E0EC] py-3 last:border-b-0 sm:grid-cols-[minmax(140px,.38fr)_minmax(190px,.62fr)_1.1fr] sm:items-baseline sm:gap-6 sm:py-5">
              <span className="s2p-display break-words text-lg font-bold uppercase leading-[.95] tracking-[0.02em] sm:text-xl lg:text-2xl" style={{ color: layer.color }}>
                {layer.label}
              </span>
              <h2 className="s2p-display text-xl font-semibold uppercase leading-tight text-[#13213A] sm:text-2xl lg:text-3xl">{layer.title}</h2>
              <div className="col-start-2 sm:col-start-3">
                <p className="text-base leading-relaxed text-[#536078] sm:text-lg lg:text-xl">{layer.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const GearOverviewSlide = () => {
  const [activeGear, setActiveGear] = useState<number | null>(null);
  const setGear = (number: number) => setActiveGear(number);
  const partClass = (number: number) => `s2p-gear-part${activeGear === number ? " is-lit" : ""}`;

  return (
    <div className="-mx-5 flex min-h-full bg-[#F7F8FB] px-5 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12">
      <div data-layout="editorial-gear-overview" className="mx-auto flex min-h-full w-full max-w-[1760px] flex-col justify-center py-3 sm:py-4">
        <div className="max-w-6xl">
          <h1 className="font-serif text-[clamp(2.75rem,4.7vw,5.25rem)] font-bold leading-[1.02] tracking-[-0.025em] text-[#1F2A44]">
            The 4 Gears of AI Usage
          </h1>
          <p className="mt-3 max-w-6xl text-base font-medium leading-relaxed text-[#6B7482] sm:text-lg lg:text-xl xl:text-2xl">
            Each gear handles a larger unit of work, from one quick question to a continuous system. Human judgement sets direction and reviews the result at every level.
          </p>
        </div>

        <div
          className={`s2p-gear-system s2p-gear-spinning mt-2 w-full ${activeGear !== null ? "is-linked" : ""}`}
          onMouseLeave={() => setActiveGear(null)}
        >
          <svg
            className="mx-auto hidden h-auto max-h-[380px] w-full max-w-full overflow-visible sm:block"
            viewBox="0 0 1160 430"
            role="img"
            aria-label="Four interlocking AI usage gears driven by human judgement. Chat handles one question or idea. Assistant handles one finished deliverable. Agents handle a multi-step objective. Loops handle a recurring outcome. Each higher gear is smaller and turns faster."
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <rect id="s2p-gear-tooth-council" x="-7" y="-58" width="14" height="17" rx="2" />
              <g id="s2p-gear-form-council">
                <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
                <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                  {Array.from({ length: 12 }, (_, index) => (
                    <use key={index} href="#s2p-gear-tooth-council" transform={index === 0 ? undefined : `rotate(${index * 30})`} />
                  ))}
                </g>
                <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
              </g>
            </defs>

            <text x="650" y="25" fill="#E4691E" style={{ fontFamily: "inherit", fontSize: 17, fontWeight: 800, letterSpacing: 2.2 }}>
              MATCH THE GEAR TO THE PROBLEM
            </text>

            {GEAR_VISUAL_ITEMS.map((gear) => (
              <g
                key={gear.number}
                className={`${partClass(gear.number)} s2p-gear-target`}
                data-gear={gear.number}
                transform={gear.transform}
                style={{ color: gear.color }}
                role="button"
                tabIndex={0}
                aria-label={`Gear ${gear.number}: ${gear.title}. ${gear.body}`}
                aria-pressed={activeGear === gear.number}
                onMouseEnter={() => setGear(gear.number)}
                onFocus={() => setGear(gear.number)}
                onBlur={() => setActiveGear(null)}
                onClick={() => setGear(gear.number)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setActiveGear(null);
                }}
              >
                <g className={`s2p-gear-rotor s2p-gear-${gear.rotor}`}>
                  <g className="s2p-gear-mesh-rotor"><use href="#s2p-gear-form-council" /></g>
                </g>
                <text className="s2p-gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ fontSize: gear.numberSize }}>
                  {gear.number}
                </text>
              </g>
            ))}

            <g fill="none" strokeWidth="2" pointerEvents="none">
              {GEAR_CONNECTORS.map((connector) => (
                <path key={connector.number} className={partClass(connector.number)} data-gear={connector.number} d={connector.path} stroke={connector.color} />
              ))}
            </g>

            <g style={{ fontFamily: "inherit" }}>
              {GEAR_LABELS.map((label) => {
                const gear = GEAR_VISUAL_ITEMS.find((item) => item.number === label.number)!;
                return (
                  <g
                    key={label.number}
                    className={`${partClass(label.number)} s2p-gear-target`}
                    data-gear={label.number}
                    onMouseEnter={() => setGear(gear.number)}
                    onClick={() => setGear(gear.number)}
                  >
                    <text x="650" y={label.titleY} fill={gear.accent} style={{ fontSize: gear.number === 0 ? 29 : 31, fontWeight: 800 }}>
                      {gear.title}
                    </text>
                    <text x="650" y={label.bodyY} fill="#6B7482" style={{ fontSize: 22, fontWeight: 600 }}>
                      {gear.body}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>

          <div className="sm:hidden">
            <svg
              className="h-auto w-full overflow-visible"
              viewBox="0 20 600 400"
              role="img"
              aria-label="Interlocking AI gears driven by human judgement, becoming smaller and faster from Chat through Assistant and Agents to Loops."
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <rect id="s2p-gear-tooth-mobile-council" x="-7" y="-58" width="14" height="17" rx="2" />
                <g id="s2p-gear-form-mobile-council">
                  <circle r="46" fill="currentColor" fillOpacity=".85" stroke="currentColor" strokeWidth="2.4" />
                  <g fill="currentColor" fillOpacity=".95" stroke="currentColor" strokeWidth="1.4">
                    {Array.from({ length: 12 }, (_, index) => (
                      <use key={index} href="#s2p-gear-tooth-mobile-council" transform={index === 0 ? undefined : `rotate(${index * 30})`} />
                    ))}
                  </g>
                  <circle r="19" fill="#fff" stroke="currentColor" strokeWidth="2" />
                </g>
              </defs>
              {GEAR_VISUAL_ITEMS.map((gear) => (
                <g key={gear.number} className={partClass(gear.number)} data-gear={gear.number} transform={gear.transform} style={{ color: gear.color }}>
                  <g className={`s2p-gear-rotor s2p-gear-${gear.rotor}`}><g className="s2p-gear-mesh-rotor"><use href="#s2p-gear-form-mobile-council" /></g></g>
                  <text className="s2p-gear-number" x="0" y="0" dy="-.08em" fill="#1F2A44" style={{ fontSize: gear.numberSize }}>{gear.number}</text>
                </g>
              ))}
            </svg>

            <p className="mt-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#E4691E]">Match the gear to the problem</p>
            <ol className="mt-2 grid gap-2" aria-label="AI usage gears">
              {[...GEAR_VISUAL_ITEMS].sort((a, b) => a.number - b.number).map((gear) => (
                <li key={gear.number}>
                  <button
                    type="button"
                    className="s2p-gear-mobile-row grid w-full grid-cols-[auto_1fr] items-center gap-x-3 rounded-xl border px-3 py-2 text-left"
                    style={{ "--gear-accent": gear.color, "--gear-ink": gear.accent } as CSSProperties}
                    aria-pressed={activeGear === gear.number}
                    onMouseEnter={() => setGear(gear.number)}
                    onFocus={() => setGear(gear.number)}
                    onBlur={() => setActiveGear(null)}
                    onClick={() => setGear(gear.number)}
                  >
                    <strong className="row-span-2 font-serif text-lg" style={{ color: gear.accent }}>{gear.number}</strong>
                    <span className="text-sm font-extrabold" style={{ color: gear.accent }}>{gear.title}</span>
                    <span className="text-xs font-semibold text-[#6B7482]">{gear.body}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

const OUTCOME_FIRST_PROMPT_SEGMENTS = [
  { label: "Collaborative framing", title: "How might we", body: "Open the problem as a possibility. This invites exploration before the team locks into the first solution." },
  { label: "Action", title: "VERB", body: "Name the exact work to perform: rewrite, compare, summarize, prioritize, design, or build." },
  { label: "Definition of done", title: "OUTCOME", body: "Describe the observable result you want, so the agent can tell what success looks like." },
  { label: "Boundary signal", title: "without", body: "Introduce the tradeoff you refuse to make. This turns a preference into a clear guardrail." },
  { label: "Rules + boundaries", title: "CONSTRAINTS", body: "State what must not change, what needs approval, and any limits on scope, time, tone, or risk." },
  { label: "Evidence signal", title: "based on", body: "Tell the agent to ground its answer in the material that follows instead of relying on default assumptions." },
  { label: "Evidence", title: "SOURCES", body: "Name the files, systems, examples, research, or data the agent should use to do the work." },
] as const;

const promptSegmentTone = (title: string) => {
  if (title === "VERB") return "orange";
  if (title === "OUTCOME") return "blue";
  if (title === "CONSTRAINTS") return "green";
  if (title === "SOURCES") return "yellow";
  return "neutral";
};

const OutcomeFirstPromptSlide = () => {
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null);
  const activeSegment = activeSegmentIndex === null ? null : OUTCOME_FIRST_PROMPT_SEGMENTS[activeSegmentIndex];

  return (
    <div
      data-layout="editorial-prompt-explorer"
      className="relative -mx-5 flex min-h-[calc(100svh-140px)] w-[calc(100%+2.5rem)] flex-col justify-center overflow-hidden bg-[#F7F8FB] px-5 py-6 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
      onMouseLeave={(event) => {
        if (!event.currentTarget.matches(":focus-within")) setActiveSegmentIndex(null);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActiveSegmentIndex(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          setActiveSegmentIndex(null);
        }
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(19,33,58,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(19,33,58,.035) 1px, transparent 1px), radial-gradient(circle at 82% 20%, rgba(0,71,255,.10), transparent 30%), radial-gradient(circle at 8% 92%, rgba(95,198,216,.14), transparent 30%)",
          backgroundSize: "64px 64px, 64px 64px, auto, auto",
        }}
      />
      <div className="relative mx-auto flex w-full max-w-[1760px] flex-col justify-center">
        <p className="flex items-center gap-4 text-xs font-extrabold uppercase tracking-[0.24em] text-[#0047FF] sm:text-sm">
          <span className="h-1 w-12 bg-[#0047FF] sm:w-16" aria-hidden="true" />
          The Magic Prompt: Outcome-Oriented AI
        </p>

        <div
          className="mt-7 flex w-full flex-wrap content-center gap-x-[0.26em] gap-y-[0.18em] font-serif text-[clamp(2.5rem,5.75vw,7.2rem)] font-semibold leading-[1.04] tracking-[-0.05em] text-[#13213A] sm:mt-9"
          role="group"
          aria-label="How might we verb outcome without constraints based on sources"
        >
          {OUTCOME_FIRST_PROMPT_SEGMENTS.map((segment, index) => {
            const tone = promptSegmentTone(segment.title);
            const displayTitle = index === 0
              ? `“${segment.title}`
              : index === OUTCOME_FIRST_PROMPT_SEGMENTS.length - 1
                ? `${segment.title}”`
                : segment.title;
            return (
              <button
                key={`${segment.title}-${index}`}
                type="button"
                className="s2p-prompt-segment"
                data-tone={tone}
                aria-controls="s2p-council-prompt-detail"
                aria-expanded={activeSegmentIndex === index}
                onMouseEnter={() => setActiveSegmentIndex(index)}
                onFocus={() => setActiveSegmentIndex(index)}
                onClick={() => setActiveSegmentIndex(index)}
              >
                {displayTitle}
              </button>
            );
          })}
        </div>

        <div className={`relative mt-6 transition-[min-height] duration-300 ${activeSegment ? "min-h-[150px] border-t border-[#D8E0EC]" : "min-h-0"}`}>
          <div
            id="s2p-council-prompt-detail"
            className={`s2p-prompt-detail absolute inset-x-0 top-3 grid min-h-[96px] grid-cols-1 items-center gap-2 overflow-hidden rounded-md px-4 py-3 transition-all duration-300 sm:grid-cols-[minmax(150px,.35fr)_1fr] sm:gap-6 sm:px-6 ${activeSegment ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none -translate-y-2 scale-[.985] opacity-0"}`}
            data-tone={activeSegment ? promptSegmentTone(activeSegment.title) : "neutral"}
            aria-live="polite"
            aria-atomic="true"
            aria-hidden={!activeSegment}
          >
            {activeSegment && (
              <>
                <div>
                  <p className="s2p-prompt-detail-label text-xs font-extrabold uppercase tracking-[0.14em] sm:text-sm">{activeSegment.label}</p>
                  <h2 className="mt-1 font-serif text-3xl font-semibold leading-none tracking-[-0.035em] text-[#13213A] sm:text-4xl">{activeSegment.title}</h2>
                </div>
                <p className="text-base leading-relaxed text-[#536078] sm:text-lg lg:text-xl">{activeSegment.body}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OUTCOME_FIRST_PROMPT_EXAMPLE = "“How might we rewrite this sales page to increase qualified calls without changing the offer, based on customer interviews, PostHog behavior, and our best-performing page?”";

const OutcomeFirstPromptExampleSlide = () => (
  <div
    data-layout="ten-x-prompt-example"
    className="relative -mx-5 flex min-h-[calc(100svh-140px)] w-[calc(100%+2.5rem)] items-center overflow-hidden bg-[#F7F8FB] px-5 py-7 text-[#13213A] sm:-mx-8 sm:w-[calc(100%+4rem)] sm:px-8 sm:py-8 lg:-mx-12 lg:w-[calc(100%+6rem)] lg:px-12"
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-60"
      aria-hidden="true"
      style={{
        backgroundImage:
          "radial-gradient(circle at 88% 12%, rgba(95,198,216,.16), transparent 32%), radial-gradient(circle at 8% 92%, rgba(0,71,255,.10), transparent 34%)",
      }}
    />
    <div className="relative mx-auto flex w-full max-w-[1760px] flex-col justify-center">
      <div className="flex items-center justify-between gap-5">
        <p className="flex items-center gap-4 text-xs font-extrabold uppercase tracking-[0.24em] text-[#0047FF] sm:text-sm lg:text-base">
          <span className="h-1 w-12 bg-[#0047FF] sm:w-16" aria-hidden="true" />
          Put it together
        </p>
      </div>
      <div className="relative mt-7 border border-[#D8E0EC] border-l-[7px] border-l-[#E4691E] bg-white px-6 py-7 text-[#171F2F] shadow-xl shadow-[#13213A]/10 sm:px-9 sm:py-9 lg:mt-9 lg:px-14 lg:py-12">
        <span className="absolute right-5 top-4 text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#8A94A3] sm:right-8 sm:top-6 sm:text-xs">Example</span>
        <blockquote className="s2p-editorial max-w-[29ch] pt-4 text-[clamp(1.65rem,3.5vw,4.4rem)] font-semibold leading-[1.08] tracking-[-0.04em] sm:pt-3">
          {OUTCOME_FIRST_PROMPT_EXAMPLE}
        </blockquote>
      </div>
    </div>
  </div>
);

const customSlides: Record<string, ReactNode> = {
  "agent-skills-explained": <AgentSkillsExplainerSlide />,
  "future-of-work": <ToolToTeamThinkingSlide />,
  "atomic-reasoning-protocol": <AtomicReasoningSlide />,
  "connections-and-tools": <ConnectionsAndToolsSlide />,
  "ai-team-management-model": <AITeamManagementModelSlide />,
  "three-layers": <ThreeLeadershipLayersSlide />,
  "four-gears": <GearOverviewSlide />,
  "how-might-we": <OutcomeFirstPromptSlide />,
  "outcome-first-prompt-example": <OutcomeFirstPromptExampleSlide />,
};

const renderCustomSlide = (slide: GuideSlide) => customSlides[slide.id] ?? null;

const S2PAIBusinessCouncilAugust = () => (
  <>
    <style dangerouslySetInnerHTML={{ __html: `
      @import url('https://fonts.googleapis.com/css2?family=Lora:wght@500;600;700&family=Oswald:wght@500;600;700&display=swap');
      .s2p-editorial { font-family: "Lora", Georgia, serif !important; }
      .s2p-display { font-family: "Oswald", "Arial Narrow", sans-serif !important; padding-bottom: 0 !important; }
      .s2p-council .bg-orange-500 { background-color: #0047ff !important; }
      .s2p-council .hover\\:bg-orange-600:hover { background-color: #0039cc !important; }
      .s2p-council .text-orange-300,
      .s2p-council .text-orange-400 { color: #5fc6d8 !important; }
      .s2p-council .border-orange-400 { border-color: #5fc6d8 !important; }
      .s2p-council .ring-orange-400 { --tw-ring-color: #5fc6d8 !important; }
      .s2p-gear-number {
        font-family: Georgia, serif;
        font-weight: 700;
        text-anchor: middle;
        dominant-baseline: central;
        pointer-events: none;
      }
      .s2p-gear-rotor,
      .s2p-gear-mesh-rotor {
        transform-box: fill-box;
        transform-origin: center;
      }
      .s2p-gear-spinning .s2p-gear-rotor {
        animation: s2p-gear-turn 4.2s cubic-bezier(.22,.75,.2,1) var(--gear-delay,0s) both;
      }
      .s2p-gear-human { --gear-turn: 38deg; --gear-delay: 0s; --gear-mesh: 30deg; }
      .s2p-gear-chat { --gear-turn: -54deg; --gear-delay: .08s; --gear-mesh: -60deg; }
      .s2p-gear-assistant { --gear-turn: 72deg; --gear-delay: .18s; --gear-mesh: 60deg; }
      .s2p-gear-agents { --gear-turn: -94deg; --gear-delay: .3s; --gear-mesh: -90deg; }
      .s2p-gear-loops { --gear-turn: 124deg; --gear-delay: .44s; --gear-mesh: 120deg; }
      @keyframes s2p-gear-turn {
        from { transform: rotate(0deg); }
        to { transform: rotate(var(--gear-turn)); }
      }
      .s2p-gear-part { transition: opacity 180ms ease, filter 180ms ease; }
      .s2p-gear-system.is-linked .s2p-gear-part { opacity: .2; }
      .s2p-gear-system.is-linked .s2p-gear-part.is-lit { opacity: 1; }
      .s2p-gear-system.is-linked .s2p-gear-part.is-lit .s2p-gear-mesh-rotor {
        animation: s2p-gear-mesh 1.6s cubic-bezier(.24,.72,.3,1) both;
      }
      @keyframes s2p-gear-mesh {
        from { transform: rotate(0deg); }
        to { transform: rotate(var(--gear-mesh)); }
      }
      .s2p-gear-target { cursor: pointer; outline: none; }
      .s2p-gear-target:focus-visible { filter: drop-shadow(0 0 5px rgba(0,71,255,.8)); }
      .s2p-gear-mobile-row {
        border-color: color-mix(in srgb, var(--gear-accent) 58%, white);
        background: color-mix(in srgb, var(--gear-accent) 13%, white);
        transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
      }
      .s2p-gear-mobile-row:hover,
      .s2p-gear-mobile-row:focus-visible,
      .s2p-gear-mobile-row[aria-pressed="true"] {
        outline: none;
        border-color: var(--gear-ink);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--gear-accent) 24%, transparent);
        transform: translateY(-1px);
      }
      .s2p-prompt-segment {
        --segment-color: #13213A;
        appearance: none;
        display: inline-flex;
        align-items: center;
        padding: .02em .09em .07em;
        border: 0;
        border-bottom: .035em solid color-mix(in srgb, var(--segment-color) 54%, transparent);
        border-radius: .12em;
        background: transparent;
        color: var(--segment-color);
        font: inherit;
        line-height: inherit;
        letter-spacing: inherit;
        cursor: help;
        transition: color 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 220ms ease, transform 220ms cubic-bezier(.2,.8,.2,1);
      }
      .s2p-prompt-segment[data-tone="orange"] { --segment-color: #C8550A; }
      .s2p-prompt-segment[data-tone="blue"] { --segment-color: #0047FF; }
      .s2p-prompt-segment[data-tone="green"] { --segment-color: #047857; }
      .s2p-prompt-segment[data-tone="yellow"] { --segment-color: #9A6700; }
      .s2p-prompt-segment:hover,
      .s2p-prompt-segment:focus-visible,
      .s2p-prompt-segment[aria-expanded="true"] {
        outline: none;
        border-color: var(--segment-color);
        background: color-mix(in srgb, var(--segment-color) 13%, transparent);
        box-shadow: 0 .08em .32em color-mix(in srgb, var(--segment-color) 16%, transparent);
        transform: translateY(-.05em);
      }
      .s2p-prompt-segment:focus-visible {
        box-shadow: 0 0 0 3px #F7F8FB, 0 0 0 6px var(--segment-color), 0 .08em .32em color-mix(in srgb, var(--segment-color) 18%, transparent);
      }
      .s2p-prompt-detail {
        --detail-color: #13213A;
        border: 1px solid color-mix(in srgb, var(--detail-color) 42%, transparent);
        border-left: 5px solid var(--detail-color);
        background: color-mix(in srgb, var(--detail-color) 7%, white);
        box-shadow: 0 12px 32px rgba(19,33,58,.10);
      }
      .s2p-prompt-detail[data-tone="orange"] { --detail-color: #C8550A; }
      .s2p-prompt-detail[data-tone="blue"] { --detail-color: #0047FF; }
      .s2p-prompt-detail[data-tone="green"] { --detail-color: #047857; }
      .s2p-prompt-detail[data-tone="yellow"] { --detail-color: #9A6700; }
      .s2p-prompt-detail-label { color: var(--detail-color); }
      @media (prefers-reduced-motion: reduce) {
        .animate-in { animation: none !important; }
        .s2p-gear-rotor,
        .s2p-gear-mesh-rotor { animation: none !important; }
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; }
      }
    ` }} />
    <SlideGuide
      name="Serve2Perform AI Business Council · August"
      trackingName="S2P AI Business Council August"
      canonicalPath="/s2p-ai-business-council-august"
      storageKey="s2p-ai-business-council-august-completed-steps"
      slideStorageKey="s2p-ai-business-council-august-current-slide"
      centerSlideContent
      seo={{
        title: "AI Business Council August | Serve2Perform + Technical Leaders",
        description: "A focused 20-minute AI Business Council session covering the Future of Work, the 4 Gears, Skills, Connectors, How Might We, and a read-only Chief of Staff Agent.",
        keywords: ["Serve2Perform", "AI Business Council", "Future of Work", "4 Gears of AI Usage", "Agent Skills", "AI Connectors", "Chief of Staff Agent"],
        image: "https://i.ytimg.com/vi/TXiaDHn1dUk/maxresdefault.jpg",
      }}
      schema={{
        description: "A focused five-part session that turns the key future-of-work ideas into one copyable Chief of Staff starter brief.",
        totalTime: "PT20M",
        supplies: ["One recurring leadership workflow", "Access to an AI workspace", "A note-taking surface"],
      }}
      cover={{
        kicker: "AI Business Council · August",
        title: "The Future of",
        accent: "AI Work",
        accentClassName: "text-[1.16em] leading-[0.88]",
        minimal: true,
        description: "Pick the right gear. Package what your team knows. Connect only what the work needs. Leave with a Chief of Staff brief you can run read-only.",
        badges: ["5 key ideas", "20 minutes", `${S2P_COUNCIL_SLIDES.length} slides`, "One starter brief"],
        hideFinishLineEyebrow: true,
        finishLineTitle: "Three practical AI upgrades",
        finishLineBody: "You’ll leave with 10× better AI answers, the “Magic Prompt,” and a Chief of Staff agent.",
      }}
      outcomes={{
        eyebrow: "The 20-minute finish line",
        heading: "Understand the model. Choose one workflow. Leave with the brief.",
        items: [
          "See why reusable Skills matter",
          "Pick the right AI gear",
          "Connect the least access required",
          "Write one outcome-first brief",
          "Copy the Chief of Staff starter",
        ],
        summary: "This is a focused leadership briefing with one practical exercise and one take-home prompt.",
      }}
      roadmapHeading="See the shift. Pick the gear. Teach the work. Write the brief. Build the teammate."
      steps={S2P_COUNCIL_STEPS}
      slides={S2P_COUNCIL_SLIDES}
      contentsExtras={[
        { slideId: "four-gears", label: "The 4 Gears" },
        { slideId: "agent-skills-explained", label: "What Agent Skills are" },
        { slideId: "future-of-work", label: "Tool → Team thinking" },
        { slideId: "ai-team-management-model", label: "AI team management" },
        { slideId: "three-layers", label: "Three leadership layers" },
        { slideId: "connections-and-tools", label: "Connections & Tools" },
        { slideId: "atomic-reasoning-protocol", label: "10× your AI answers" },
        { slideId: "how-might-we", label: "How Might We" },
        { slideId: "outcome-first-prompt-example", label: "Outcome-first example" },
        { slideId: "chief-of-staff", label: "Chief of Staff brief" },
      ]}
      branding={{
        rootClassName: "s2p-council",
        chrome: "light",
        logo: {
          src: "/serve2perform-logo.png",
          alt: "Serve2Perform",
          href: "/s2p-ai-business-council-august",
          ariaLabel: "Return to the AI Business Council opening slide",
          imageClassName: "h-6 w-auto sm:h-7",
        },
        paperBackdrop: "radial-gradient(circle at 8% 16%, rgba(0,71,255,.10), transparent 24%), radial-gradient(circle at 88% 82%, rgba(95,198,216,.12), transparent 25%)",
        darkBackdrop: "radial-gradient(circle at 82% 18%, rgba(95,198,216,.16), transparent 26%), radial-gradient(circle at 10% 90%, rgba(0,71,255,.18), transparent 28%), linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
      }}
      renderCustomSlide={renderCustomSlide}
      finish={{
        eyebrow: "You have the starter",
        heading: "Run it once. Review it. Teach it.",
        body: "Start read-only. Review the evidence and ranking. Save what worked. Update the Skill. Expand access only when the value is real and the owner is clear.",
        cards: [
          {
            eyebrow: "Build",
            title: "Go deeper with ELEVATE",
            body: "Work through workflow selection, integration, Skills, stop rules, validation, and handoff.",
            href: "/s2p-elevate-ai",
          },
          {
            eyebrow: "Practice",
            title: "Build the feedback loop",
            body: "Turn review into reusable instructions, choose the cadence, and keep judgement visible.",
            href: "/s2p-elevate-ai#feedback-and-schedule",
          },
          {
            eyebrow: "Watch",
            title: "The Future of Work",
            body: "Watch the source video, then choose the first piece of team expertise you will package.",
            href: FUTURE_OF_WORK_VIDEO_URL,
          },
        ],
      }}
    />
  </>
);

export default S2PAIBusinessCouncilAugust;
